/**
 * Google Places API (New) Service
 * Provides REST API functions for Google Places with fallback strategy
 * Requirements: 4.3, 3.5
 */

/**
 * Get Google Places API key from environment variables
 * Uses staging key for development, production key for production
 * @returns {string} API key
 */
export const getApiKey = () => {
  const isDevelopment =
    process.env.REACT_APP_APP_ENV === "development" ||
    process.env.NODE_ENV === "development";

  return isDevelopment
    ? process.env.REACT_APP_GOOGLE_WEB_API_KEY_STAGING
    : process.env.REACT_APP_GOOGLE_WEB_API_KEY;
};

/**
 * Create standard headers for Google Places API requests
 * @returns {Object} Headers object
 */
export const createApiHeaders = (fieldMask = null) => {
  const headers = {
    "Content-Type": "application/json",
    "X-Goog-Api-Key": getApiKey(),
  };

  if (fieldMask) {
    headers["X-Goog-FieldMask"] = fieldMask;
  }

  return headers;
};

/**
 * Handle API response and errors consistently
 * @param {Response} response - Fetch response object
 * @returns {Promise<Object>} Parsed JSON response
 * @throws {Error} Network or API errors
 */
export const handleApiResponse = async (response) => {
  console.log('api response',response);
  console.log('api response.ok',response.ok);
  
  if (!response.ok) {
    const errorText = await response.text();
    console.error(`Google Places API error: ${response.status}`, errorText);
    throw new Error(
      `API request failed: ${response.status} ${response.statusText}`
    );
  }

  const responseText = await response.text();
  if (!responseText || responseText.trim().length === 0) {
    console.error("Google Places API returned empty response");
    throw new Error("Empty response from API");
  }

  try {
    return JSON.parse(responseText);
  } catch (parseError) {
    console.error("Failed to parse API response:", parseError);
    throw new Error("Invalid JSON response from API");
  }
};

/**
 * Search places using Google Places Autocomplete API
 * @param {string} query - Search query
 * @returns {Promise<Object>} API response with suggestions
 */
export const searchAutocomplete = async (query) => {
  const apiKey = getApiKey();

  if (!apiKey) {
    throw new Error("Google Places API key is not configured");
  }

  const requestBody = {
    input: query,
    includedRegionCodes: ["NG"], // Restrict to Nigeria per requirements 4.1, 4.2
  };

  const response = await fetch(
    "https://places.googleapis.com/v1/places:autocomplete",
    {
      method: "POST",
      headers: createApiHeaders(
        "suggestions.placePrediction.placeId,suggestions.placePrediction.text,suggestions.placePrediction.structuredFormat"
      ),
      body: JSON.stringify(requestBody),
    }
  );

  return handleApiResponse(response);
};

/**
 * Search places using Google Places SearchText API (fallback)
 * @param {string} query - Search query
 * @returns {Promise<Object>} API response with places
 */
export const searchText = async (query) => {
  const apiKey = getApiKey();

  if (!apiKey) {
    throw new Error("Google Places API key is not configured");
  }

  const requestBody = {
    textQuery: query,
    regionCode: "NG", // Restrict to Nigeria per requirements 4.1, 4.2
  };

  const response = await fetch(
    "https://places.googleapis.com/v1/places:searchText",
    {
      method: "POST",
      headers: createApiHeaders(
        "places.id,places.displayName,places.formattedAddress,places.location,places.addressComponents,places.types"
      ),
      body: JSON.stringify(requestBody),
    }
  );

  return handleApiResponse(response);
};

/**
 * Get place details using Google Places Details API
 * @param {string} placeId - Place ID from autocomplete or search
 * @returns {Promise<Object>} Place details
 */
export const getPlaceDetails = async (placeId) => {
  const apiKey = getApiKey();

  if (!apiKey) {
    throw new Error("Google Places API key is not configured");
  }

  if (!placeId) {
    throw new Error("Place ID is required");
  }
console.log('we here in getplacedetails');

  const response = await fetch(
    `https://places.googleapis.com/v1/places/${placeId}`,
    {
      method: "GET",
      headers: createApiHeaders(
        "id,displayName,formattedAddress,location,addressComponents,types,viewport"
      ),
    }
  );

  console.log('response place',response);
  
  return handleApiResponse(response);
};

/**
 * Log API errors for debugging (requirement 3.5)
 * @param {string} operation - API operation that failed
 * @param {Error} error - Error object
 * @param {Object} context - Additional context for debugging
 */
export const logApiError = (operation, error, context = {}) => {
  console.error(`Google Places API Error - ${operation}:`, {
    message: error.message,
    stack: error.stack,
    context,
    timestamp: new Date().toISOString(),
  });
};

/**
 * Dual API search strategy - tries Autocomplete first, falls back to SearchText
 * Implements the fallback strategy as specified in requirements 1.2, 3.1
 * @param {string} query - Search query
 * @returns {Promise<Object>} Unified response format
 */
export const searchPlaces = async (query) => {
  if (!query || query.trim().length === 0) {
    throw new Error("Search query is required");
  }

  let autocompleteResults = null;
  let useAutocomplete = true;

  try {
    // Try Autocomplete API first (better for address searches)
    autocompleteResults = await searchAutocomplete(query);

    // Check if autocomplete returned useful results
    if (
      !autocompleteResults.suggestions ||
      autocompleteResults.suggestions.length === 0
    ) {
      useAutocomplete = false;
    }
  } catch (autocompleteError) {
    logApiError("searchAutocomplete", autocompleteError, { query });
    useAutocomplete = false;
  }

  // Fallback to SearchText API if autocomplete didn't work
  if (!useAutocomplete) {
    try {
      const searchTextResults = await searchText(query);

      // Transform searchText results to match autocomplete format
      if (searchTextResults.places && searchTextResults.places.length > 0) {
        return {
          predictions: searchTextResults.places.map((place) => ({
            place_id: place.id,
            description:
              place.formattedAddress || place.displayName?.text || "",
            structured_formatting: {
              main_text: place.displayName?.text || "",
              secondary_text: place.formattedAddress || "",
            },
            // Store full place data for efficiency
            fullData: place,
          })),
          source: "searchText",
        };
      } else {
        return { predictions: [], source: "searchText" };
      }
    } catch (searchTextError) {
      logApiError("searchText", searchTextError, { query });
      throw new Error("Both Autocomplete and SearchText APIs failed");
    }
  }

  // Transform autocomplete results to unified format
  const predictions = autocompleteResults.suggestions
    .filter((suggestion) => suggestion.placePrediction)
    .map((suggestion) => {
      const prediction = suggestion.placePrediction;
      const mainText = prediction.structuredFormat?.mainText?.text || "";
      const secondaryText =
        prediction.structuredFormat?.secondaryText?.text || "";
      const fullText =
        prediction.text?.text || `${mainText} ${secondaryText}`.trim();

      return {
        place_id: prediction.placeId,
        description: fullText,
        structured_formatting: {
          main_text: mainText || fullText,
          secondary_text: secondaryText || "",
        },
      };
    });

  return {
    predictions,
    source: "autocomplete",
  };
};
